'use client'
import "@/components/ReviewDetails/ReviewDetails.css";
import React, { useState, useEffect, useRef } from "react";
import { generateGoogleReviewResponse, postGoogleReviewResponse } from "@/API/Review/responsesAPI";

/**
 * @description The ReviewDetails component is responsible for displaying the details of a single review. It also shows the AI response to the review.
 * @param {Object} review - The review object containing details such as content, rating, and author.
 * @returns {JSX.Element} The ReviewDetails component.
 */
export default function ReviewDetails({ review, setReviewDetails }) {
  // Status enum for tracking the state of the component. Used to show different UI states.
  const statuses = Object.freeze({
    DISPLAY: "display",
    EDIT: "edit",
  });
  
  // State to hold the AI response.
  const [aiResponse, setAiResponse] = useState("");

  // State to send response
  const [responseApplied, setResponseApplied] = useState(false);

  // State show what kind of display
  const [displayStatus, setDisplayStatus] = useState(statuses.DISPLAY);

  // State to hold things to be added
  const [toBeAdded, setToBeAdded] = useState("");

  // State to hold things to be substracted
  const [toBeSubtracted, setToBeSubtracted] = useState("");

  // State to hold responses for AI to avoid similarities to
  const [responsesToAvoid, setResponsesToAvoid] = useState("");

  // TODO: this shouldn't be needed anymore since the modal is in the parent component
  // State for popup modal
  const [popUpOpen, setPopupOpen] = useState(false);
  const [popUpTitle, setPopupTitle] = useState("");
  const [popUpMessage, setPopupMessage] = useState("");

  // State to track viewing existing response or generating new one
  const [viewExisting, setViewExisting] = useState(Boolean(review.hasReply));

  // Ref for textareas to auto expand
  const ResponseTextareaRef = useRef(null);
  const ResponseAddTextareaRef = useRef(null);
  const ResponseSubtractTextareaRef = useRef(null);

  // Function to fetch AI response for the review.
  async function fetchAIResponse() {
    // Add on the current review's responses to avoid if they exist
    const currentResponsesToAvoid = responsesToAvoid;
    setResponsesToAvoid(currentResponsesToAvoid + "\n" + aiResponse); 

    // To tell user it's loading.
    setAiResponse("Generating response...");
    const response = await generateGoogleReviewResponse(review.id, review.content, review.rating, toBeAdded, toBeSubtracted, currentResponsesToAvoid);
    if (response.success) {
      setAiResponse(response.response);
    } else {
      setAiResponse("Failed to generate response. Please try again.");
    }
  }

  async function postResponse() {
    const result = await postGoogleReviewResponse(review.id, aiResponse);
    if (!result.success) {
      //alert("Failed to post response: " + result.message);
      setPopupTitle("Error");
      setPopupMessage(result.message || "Failed to post response. Please try again.");
      setPopupOpen(true);
      return;
    }

    setResponseApplied(true);
    setPopupTitle("Success");
    setPopupMessage(result.message || "Response posted successfully!");
    setPopupOpen(true);
  }

  // Function to start a new response, resetting the view and display status.
  function startNewResponse() {
    setViewExisting(false);
    setDisplayStatus(statuses.EDIT);
    fetchAIResponse();
  }

  // Fetch AI response when the component mounts or show existing response if it exists.
  useEffect(() => {
    if (review.hasReply && review.reply) {
      setViewExisting(true);
      setAiResponse(review.reply);
    } else {
      setViewExisting(false);
      fetchAIResponse();
    }
  }, [review]);

  // Auto expand response box when aiResponse is generated
  useEffect(() => {
    if (ResponseTextareaRef.current) {
      ResponseTextareaRef.current.style.height = "auto"; // Reset height
      ResponseTextareaRef.current.style.height = ResponseTextareaRef.current.scrollHeight + "px"; // Set to scrollHeight
    }

    if (ResponseAddTextareaRef.current) {
      ResponseAddTextareaRef.current.style.height = "auto";
      ResponseAddTextareaRef.current.style.height = ResponseAddTextareaRef.current.scrollHeight + "px"; // Set to scrollHeight
    }

    if (ResponseSubtractTextareaRef.current) {
      ResponseSubtractTextareaRef.current.style.height = "auto";
      ResponseSubtractTextareaRef.current.style.height = ResponseSubtractTextareaRef.current.scrollHeight + "px"; // Set to scrollHeight
    }
  }, [aiResponse, toBeAdded, toBeSubtracted]);


  // Handle posting the response.
  if (responseApplied) {
    return (
      <div className="reviewDetails">
        <div className="reviewDetails-header">
          <h2>Response Applied</h2>
          <button
            className="close-button"
            onClick={() => setReviewDetails(null)}
            aria-label="Close Review Details"
          >
            ✕
          </button>
        </div>
        <div className="reviewDetails-actions-div">
          <p>The response to {review.name}'s review has been submitted!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reviewDetails">
      <div
        className="reviewDetails-header"
      >
        <h2>Review by {review.name}</h2>
        <button
          className="close-button"
          onClick={() => setReviewDetails(null)}
          aria-label="Close Review Details"
        >
          ✕
        </button>
      </div>

      <div className="rating">
        {"★".repeat(review.rating)}{"☆".repeat(5 - review.rating)}
      </div>

      <div className="reviewDetails-scrollable">
        <p>{review.content}</p>

        {/* Show the existing posted response if one exists */}
        {viewExisting ? (
          <>
            <h3>Existing Response:</h3>
            <p className="existing-response">{review.reply}</p>
            <div className="reviewDetails-actions-div">
              <button className="reviewActions" onClick={startNewResponse}>New Response</button>
            </div>
          </>
        ) : (
          <>
            <h3>AI Response:</h3>
            <textarea
              ref={ResponseTextareaRef}
              value={aiResponse}
              onChange={e => {
                setAiResponse(e.target.value);
                e.target.style.height = "auto"; // Reset height
                e.target.style.height = e.target.scrollHeight + "px"; // Set to scrollHeight
              }}
            />

            {/* For editing the review */}
            {displayStatus === statuses.EDIT && (
              <>
                <textarea
                  className="response-edit-textarea"
                  ref={ResponseAddTextareaRef}
                  placeholder="Things to add to the response, separated by commas. Can be empty"
                  onChange={e => {
                    setToBeAdded(e.target.value);
                  }}
                />
                <textarea
                  className="response-edit-textarea"
                  ref={ResponseSubtractTextareaRef}
                  placeholder="Things to remove from the response, separated by commas. Can be empty"
                  onChange={e => {
                    setToBeSubtracted(e.target.value);
                  }}
                />
                <div className="reviewDetails-actions-div">
                  <button className="reviewActions" onClick={() => fetchAIResponse()}>Regenerate</button>
                </div>
                <div className="reviewDetails-actions-div">
                  <button className="reviewActions" onClick={postResponse}>Respond</button>
                </div>
              </>
            )}

            {displayStatus === statuses.DISPLAY && (
              <div className="reviewDetails-actions-div">
                <button className="reviewActions" onClick={() => setDisplayStatus(statuses.EDIT)}>New Response</button>
                <button className="reviewActions" onClick={postResponse}>Respond</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
